import {
  Alert,
  Box,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Cached, ContentCopy, DeleteOutline } from "@mui/icons-material";
import { useEffect, useMemo, useState } from "react";
import { useSnackbar } from "notistack";
import Page from "../../core/components/Page";
import { getApiKeys, issueApiKey, revokeApiKey } from "../../services/apiKey.service";

type ApiKeyDialogMode = "create" | "rotate";

const API_KEY_NAME_MAX_LENGTH = 128;

function extractApiKeysFromResponse(res: any): any[] {
  const payload = res?.data?.data ?? res?.data;

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.apiKeys)) return payload.apiKeys;
  if (Array.isArray(payload?.api_keys)) return payload.api_keys;
  if (Array.isArray(payload?.data)) return payload.data;

  if (payload && typeof payload === "object" && Object.keys(payload).length > 0) {
    return [payload];
  }

  return [];
}

function getRowId(row: any, index: number): string {
  return String(
    row?.id ??
      row?.keyId ??
      row?.apiKeyId ??
      row?.api_key_id ??
      `${row?.name ?? "row"}-${row?.createdAt ?? row?.created_at ?? index}-${index}`
  );
}

function getRowServerId(row: any): string | undefined {
  const id = row?.id ?? row?.keyId ?? row?.apiKeyId ?? row?.api_key_id;
  return id == null ? undefined : String(id);
}

function getExpiresAtText(row: any): string {
  const expiresAt = row?.expiresAt ?? row?.expires_at;
  if (expiresAt == null || expiresAt === "") return "Süresiz";
  return String(expiresAt);
}

function extractKeyFromResponse(res: any): string | undefined {
  return (
    res?.data?.data?.apiKey ||
    res?.data?.data?.key ||
    res?.data?.apiKey ||
    res?.data?.key
  );
}

function maskKey(key: string) {
  if (!key) return "";
  if (key.length <= 8) return "********";
  return `${key.slice(0, 4)}********${key.slice(-4)}`;
}

export default function ApiKeys() {
  const { enqueueSnackbar } = useSnackbar();

  const [apiKeys, setApiKeys] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<ApiKeyDialogMode>("create");

  const [newKeyOpen, setNewKeyOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<string | undefined>(undefined);

  const [name, setName] = useState("");
  const [expiresInDays, setExpiresInDays] = useState<number | null>(30);
  const [rotateKeyId, setRotateKeyId] = useState<string | null>(null);

  const expiresOptions = useMemo(
    () => [
      { label: "Süresiz", value: "never" as const },
      { label: "7 gün", value: 7 },
      { label: "30 gün", value: 30 },
      { label: "90 gün", value: 90 },
      { label: "180 gün", value: 180 },
      { label: "365 gün", value: 365 },
    ],
    []
  );

  const openDialog = (
    mode: ApiKeyDialogMode,
    preset?: {
      name?: string;
      expiresInDays?: number | null;
      rotateKeyId?: string | null;
    }
  ) => {
    setDialogMode(mode);
    setName(preset?.name ?? "");
    setExpiresInDays(preset?.expiresInDays ?? 30);
    setRotateKeyId(preset?.rotateKeyId ?? null);
    setCreateOpen(true);
  };

  const fetchApiKeys = async () => {
    setLoading(true);
    const res = await getApiKeys();
    setApiKeys(extractApiKeysFromResponse(res));
    setLoading(false);
  };

  useEffect(() => {
    fetchApiKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      enqueueSnackbar("Anahtar adı zorunludur.", { variant: "error" });
      return;
    }
    if (trimmedName.length > API_KEY_NAME_MAX_LENGTH) {
      enqueueSnackbar(
        `Anahtar adı en fazla ${API_KEY_NAME_MAX_LENGTH} karakter olabilir.`,
        { variant: "error" }
      );
      return;
    }

    const payload: any = {
      name: trimmedName,
      ...(expiresInDays == null ? { neverExpires: true } : { expiresInDays }),
      ...(dialogMode === "rotate" && rotateKeyId ? { rotateKeyId } : {}),
    };

    setLoading(true);
    const res = await issueApiKey(payload);
    setLoading(false);

    if (res?.status && res.status >= 200 && res.status < 300) {
      const key = extractKeyFromResponse(res);
      setCreateOpen(false);
      setDialogMode("create");
      setRotateKeyId(null);
      setName("");
      setExpiresInDays(30);
      await fetchApiKeys();

      if (key) {
        setCreatedKey(key);
        setNewKeyOpen(true);
      } else {
        enqueueSnackbar(
          dialogMode === "rotate" ? "API anahtarı yenilendi." : "API anahtarı oluşturuldu.",
          { variant: "success" }
        );
      }
    } else {
      enqueueSnackbar(
        dialogMode === "rotate" ? "API anahtarı yenilenemedi." : "API anahtarı oluşturulamadı.",
        { variant: "error" }
      );
    }
  };


  const handleRevoke = async (row: any) => {
    const id = getRowServerId(row);
    if (!id) {
      enqueueSnackbar("Bu anahtar için id bulunamadı.", { variant: "error" });
      return;
    }

    const ok = window.confirm("Bu API anahtarını iptal etmek istiyor musunuz?");
    if (!ok) return;

    setLoading(true);
    const res = await revokeApiKey(id);
    setLoading(false);

    if (res?.status && res.status >= 200 && res.status < 300) {
      enqueueSnackbar("API anahtarı iptal edildi.", { variant: "success" });
      await fetchApiKeys();
    } else {
      enqueueSnackbar("API anahtarı iptal edilemedi.", { variant: "error" });
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar("Kopyalandı.", { variant: "success" });
    } catch {
      enqueueSnackbar("Kopyalanamadı.", { variant: "error" });
    }
  };

  return (
    <Page title="API Anahtarları | Boardnote">
      <Card sx={{ mx: 3, p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
          <CardHeader title="API Anahtarları" />
          <Button variant="contained" onClick={() => openDialog("create")} disabled={loading}>
            Yeni API Anahtarı
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mb: 2 }}>
          Güvenlik sebebiyle anahtar değeri sadece oluşturma sırasında gösterilir.
        </Alert>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ad</TableCell>
                <TableCell>Oluşturulma</TableCell>
                <TableCell>Bitiş</TableCell>
                <TableCell>Durum</TableCell>
                <TableCell align="right">İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {apiKeys?.map((row: any, index: number) => {
                const rowKey = getRowId(row, index);
                const status = String(row?.status ?? (row?.revokedAt || row?.revoked_at ? "REVOKED" : "ACTIVE"));
                const isRevoked = status.toUpperCase() === "REVOKED";
                const hasId = Boolean(getRowServerId(row));

                return (
                  <TableRow key={rowKey}>
                    <TableCell
                      sx={{
                        maxWidth: 280,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                      title={row?.name ?? undefined}
                    >
                      {row?.name ?? "-"}
                    </TableCell>
                    <TableCell>{row?.createdAt ?? row?.created_at ?? "-"}</TableCell>
                    <TableCell>{getExpiresAtText(row)}</TableCell>
                    <TableCell>{status}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label="rotate"
                        title="Yenile"
                        onClick={() =>
                          openDialog("rotate", {
                            name: row?.name,
                            expiresInDays:
                              row?.expiresAt == null && row?.expires_at == null ? null : 30,
                            rotateKeyId: getRowServerId(row) ?? null,
                          })
                        }
                        disabled={loading || !row?.name || isRevoked}
                      >
                        <Cached />
                      </IconButton>

                      <IconButton
                        aria-label="revoke"
                        title="İptal Et"
                        onClick={() => handleRevoke(row)}
                        disabled={loading || !hasId}
                      >
                        <DeleteOutline />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}

              {!apiKeys?.length && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography color="text.secondary">
                      {loading ? "Yükleniyor..." : "Henüz API anahtarı yok."}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setDialogMode("create");
          setRotateKeyId(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{dialogMode === "rotate" ? "API Anahtarı Yenile" : "Yeni API Anahtarı"}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Anahtar Adı"
              value={name}
              onChange={(e) => setName(e.target.value)}
              inputProps={{ maxLength: API_KEY_NAME_MAX_LENGTH }}
              helperText={`${name.length}/${API_KEY_NAME_MAX_LENGTH}`}
              fullWidth
              required
            />

            <TextField
              select
              label="Geçerlilik Süresi"
              value={expiresInDays == null ? "never" : expiresInDays}
              onChange={(e) =>
                setExpiresInDays(e.target.value === "never" ? null : Number(e.target.value))
              }
              fullWidth
            >
              {expiresOptions.map((opt) => (
                <MenuItem key={String(opt.value)} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateOpen(false);
              setDialogMode("create");
            }}
            color="inherit"
          >
            İptal
          </Button>
          <Button onClick={handleCreate} variant="contained" disabled={loading}>
            {dialogMode === "rotate" ? "Yenile" : "Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={newKeyOpen} onClose={() => setNewKeyOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>API Anahtarı</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Bu anahtar değerini güvenli bir yerde saklayın. Tekrar gösterilmeyebilir.
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 2,
              borderRadius: 1,
              bgcolor: "background.neutral",
              wordBreak: "break-all",
            }}
          >
            <Typography sx={{ flex: 1 }}>
              {createdKey ? createdKey : "-"}
            </Typography>
            <IconButton
              aria-label="copy"
              onClick={() => createdKey && copyToClipboard(createdKey)}
              disabled={!createdKey}
            >
              <ContentCopy />
            </IconButton>
          </Box>
          {createdKey ? (
            <Typography variant="caption" color="text.secondary" display="block" mt={1}>
              Maskeli görünüm: {maskKey(createdKey)}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewKeyOpen(false)} variant="contained">
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
